/**
 * Time zone assumed for an entity without one (the same as the Backend default).
 */
export const DEFAULT_TIME_ZONE = 'Europe/Lisbon';

/**
 * Time zones used when the browser cannot list the supported ones.
 */
const FALLBACK_TIME_ZONES = [
  'Atlantic/Azores',
  'Atlantic/Madeira',
  'Europe/Lisbon',
  'Europe/Madrid',
  'Europe/London',
  'America/Sao_Paulo',
  'UTC',
];

/**
 * Array with all the time zones (TZ Database names, e.g. Europe/Lisbon) known by the browser.
 */
export const timeZones: Array<string> = (() => {
  try {
    const supported = (
      Intl as typeof Intl & {supportedValuesOf: (key: 'timeZone') => string[]}
    ).supportedValuesOf('timeZone');

    return supported.includes('UTC') ? supported : [...supported, 'UTC'];
  } catch {
    return FALLBACK_TIME_ZONES;
  }
})();

/**
 * Offset of a time zone to UTC at the given moment, in minutes (e.g. 60 for "+01:00").
 */
function getTimeZoneOffsetMinutes(timeZone: string, date: Date): number | undefined {
  try {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      hourCycle: 'h23',
      year: 'numeric',
      month: 'numeric',
      day: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
      second: 'numeric',
    }).formatToParts(date);
    const get = (type: string) => Number(parts.find((part) => part.type === type)?.value);
    const localAsUtc = Date.UTC(
      get('year'),
      get('month') - 1,
      get('day'),
      get('hour'),
      get('minute'),
      get('second')
    );

    return Math.round((localAsUtc - Math.floor(date.getTime() / 1000) * 1000) / 60000);
  } catch {
    return undefined;
  }
}

/**
 * Readable label of a time zone, e.g. "Europe/San_Marino" becomes "Europe/San Marino (+01:00)".
 * The offset is the current one, so it follows the daylight saving time.
 */
export function formatTimeZoneLabel(timeZone: string, date: Date = new Date()): string {
  const name = timeZone.replace(/_/g, ' ');
  const offsetMinutes = getTimeZoneOffsetMinutes(timeZone, date);

  if (offsetMinutes === undefined) return name;

  const absolute = Math.abs(offsetMinutes);
  const hours = String(Math.floor(absolute / 60)).padStart(2, '0');
  const minutes = String(absolute % 60).padStart(2, '0');

  return `${name} (${offsetMinutes < 0 ? '-' : '+'}${hours}:${minutes})`;
}

/**
 * Sorts the time zones by their current offset to UTC, from the most negative to the most positive
 * (e.g. -03:00, -02:00, +00:00, +01:00), and by name when the offset is the same.
 */
export function sortTimeZonesByOffset(
  timeZonesToSort: Array<string>,
  date: Date = new Date()
): Array<string> {
  const offsets = new Map(
    timeZonesToSort.map((timeZone) => [timeZone, getTimeZoneOffsetMinutes(timeZone, date) ?? 0])
  );

  return [...timeZonesToSort].sort(
    (a, b) => offsets.get(a)! - offsets.get(b)! || a.localeCompare(b)
  );
}
